import type { ReactNode } from 'react';
import { Link } from 'react-router';

import { buildRoute } from '@/constants/routes';
import { cn } from '@/utils/cn';

import { type LinkedMember, personLabel } from '../../schemas/attendance.schema';
import { PersonAvatar } from '../PersonAvatar';
import styles from './PersonRow.module.css';

export interface PersonRowProps {
  person: { member: LinkedMember | null; name: string | null; deviceUserId: string };
  /** Under the name: member code and status, or why they aren't linked. */
  meta: ReactNode;
  /** On the right: a time or date. */
  aside?: ReactNode;
  /** After the row: a button such as Link or Call. */
  action?: ReactNode;
}

/**
 * One person in a list: photo, name, a line about them and a time on the right. A linked member's
 * row opens their profile; an unlinked machine user's row is plain text.
 */
export function PersonRow({ person, meta, aside, action }: PersonRowProps) {
  const body = (
    <>
      <PersonAvatar person={person} size="md" />
      <span className={styles.text}>
        <span className={styles.name}>{personLabel(person)}</span>
        <span className={styles.meta}>{meta}</span>
      </span>
      {aside !== undefined && <span className={styles.aside}>{aside}</span>}
    </>
  );

  return (
    <li className={styles.item}>
      {person.member ? (
        <Link
          to={buildRoute.memberDetail(person.member.id)}
          className={cn(styles.row, styles.link)}
        >
          {body}
        </Link>
      ) : (
        <div className={styles.row}>{body}</div>
      )}
      {action}
    </li>
  );
}
